import { CheckCircle, XCircle } from 'lucide-react'
import type { ConfirmCheckout } from '@/modules/auth/use-cases'
import { AuthLayout } from '@/modules/auth/ui/components'
import { LoadingSpinner } from '@/shared/ui/components'
import { Button } from '@/shared/ui/primitives'
import { useRegisterCompletePage } from './hooks/use-register-complete-page'

export type RegisterCompletePageProps = {
  onConfirmCheckout: ConfirmCheckout
}

export function RegisterCompletePage({ onConfirmCheckout }: RegisterCompletePageProps) {
  const vm = useRegisterCompletePage({ onConfirmCheckout })

  if (vm.status === 'loading') {
    return (
      <AuthLayout
        title="Verifying your payment…"
        subtitle="Creating your organization. Please don't close this window."
      >
        <div
          data-testid="register-complete-loading"
          className="flex flex-col items-center justify-center gap-3 py-6"
        >
          <LoadingSpinner size="lg" />
        </div>
      </AuthLayout>
    )
  }

  if (vm.status === 'error') {
    return (
      <AuthLayout title="Setup Failed">
        <div
          data-testid="register-complete-error"
          className="flex flex-col items-center gap-4 text-center"
        >
          <XCircle className="size-12 text-destructive" />
          <p role="alert" className="text-sm text-muted-foreground">
            {vm.errorMessage}
          </p>
          <p className="text-xs text-muted-foreground">
            Your payment was received. Contact{' '}
            <a
              href="mailto:contact@blindbook.com"
              className="font-medium text-primary underline-offset-4 hover:underline"
            >
              support
            </a>{' '}
            and we&apos;ll get you set up.
          </p>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title="You're all set!">
      <div
        data-testid="register-complete-success"
        className="flex flex-col items-center gap-4 text-center"
      >
        <CheckCircle className="size-12 text-primary" />
        {vm.source === 'web' ? (
          <>
            <p className="text-sm text-muted-foreground">
              Your account has been set up. Head to your dashboard to get started.
            </p>
            <Button
              type="button"
              className="w-full"
              onClick={vm.handleContinue}
              data-testid="register-complete-continue"
            >
              Continue to BlindBook
            </Button>
          </>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <p className="text-sm text-muted-foreground">
              All set! Returning to BlindBook…
            </p>
            <LoadingSpinner size="sm" />
          </div>
        )}
      </div>
    </AuthLayout>
  )
}
